import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import RequestAccess from "@/pages/auth/RequestAccess";
import AuthCallback from "@/pages/auth/AuthCallback";
import AccessRequired from "@/pages/auth/AccessRequired";
import AcceptInviteAlias from "@/pages/auth/AcceptInviteAlias";

import AppShell from "@/components/layout/AppShell";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

import ClientHome from "@/pages/client/ClientHome";
import Companion from "@/pages/client/Companion";
import CheckIn from "@/pages/client/CheckIn";
import Plan from "@/pages/client/Plan";
import Insights from "@/pages/client/Insights";
import Rhythm from "@/pages/client/Rhythm";
import Victories from "@/pages/client/Victories";
import Memories from "@/pages/client/Memories";
import Safety from "@/pages/client/Safety";
import Settings from "@/pages/client/Settings";

import AdminHome from "@/pages/admin/AdminHome";
import AdminUsers from "@/pages/admin/AdminUsers";
import { AdminConversations, AdminFlagged } from "@/pages/admin/AdminConversations";
import AdminInvites from "@/pages/admin/AdminInvites";
import AdminBeta from "@/pages/admin/AdminBeta";
import AdminUsage from "@/pages/admin/AdminUsage";
import AdminFeedback from "@/pages/admin/AdminFeedback";

import PlatformHome from "@/pages/platform/PlatformHome";
import {
  PlatformPeople,
  PlatformTeams,
  PlatformLocations,
  PlatformDepartments,
  PlatformInvites,
  PlatformAudit,
  PlatformAnalytics,
  PlatformWellbeing,
} from "@/pages/platform/PlatformPages";

import CareHome from "@/pages/care/CareHome";
import { CareClients, CareClientDetail, CareAlerts, CareDossier } from "@/pages/care/CarePages";
import Onboarding from "@/pages/client/Onboarding";
import { NightReflection, QuietReturns } from "@/pages/client/ReflectionPages";
import Craving from "@/pages/client/Craving";
import { Ambient, Proactive } from "@/pages/client/AmbientProactive";
import AdminShell from "@/components/layout/AdminShell";
import AdminDashboard from "@/pages/admin_hr/AdminDashboard";
import Organizations from "@/pages/admin_hr/Organizations";
import Teams from "@/pages/admin_hr/Teams";
import Invitations from "@/pages/admin_hr/Invitations";
import Members from "@/pages/admin_hr/Members";
import Analytics from "@/pages/admin_hr/Analytics";
import AdminSettings from "@/pages/admin_hr/AdminSettings";

import PublicLayout from "@/components/layout/PublicLayout";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Cookies from "@/pages/public/Cookies";
import AiSafety from "@/pages/public/AiSafety";
import PrivacyChoices from "@/pages/public/PrivacyChoices";
import PublicSafety from "@/pages/public/Safety";
import Support from "@/pages/public/Support";
import Contact from "@/pages/public/Contact";
import DeleteAccount from "@/pages/public/DeleteAccount";
import Entry from "@/pages/auth/Entry";
import Download from "@/pages/public/Download";
import AppDownloadPrompt from "@/components/common/AppDownloadPrompt";
import Billing from "@/pages/admin_hr/Billing";
import ConsentCenter from "@/pages/client/ConsentCenter";
import { OwnerShell, OhShell } from "@/components/layout/PortalShells";
import { OwnerDashboard, OwnerOrganizations } from "@/pages/owner/OwnerPages";
import OwnerOrgDetail from "@/pages/owner/OwnerOrgDetail";
import { OwnerBilling, OwnerRoles, OwnerAudit } from "@/pages/owner/OwnerAdminPages";
import { OhHome, OhClients, OhClientDetail } from "@/pages/oh/OhPages";

/** Intercept OAuth callback synchronously during render (before ProtectedRoute)
 *  to avoid race conditions with the AuthProvider's /auth/me bootstrap. */
const PROMPT_FREE_PREFIXES = ["/app", "/admin", "/owner", "/oh", "/onboarding", "/download", "/delete-account", "/reset-password", "/invite", "/accept-invite"];

function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  const showPrompt = !PROMPT_FREE_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  return (
    <>
    {showPrompt && <AppDownloadPrompt />}
    <Routes>
      {/* Public/auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Android App Link / B2B invitation: token-less /invite is NOT an invite — send to the public entry. */}
      <Route path="/invite" element={<Navigate to="/" replace />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/accept-invite" element={<AcceptInviteAlias />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/access-required" element={<AccessRequired />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

      {/* Public unauthenticated pages — accessible without login.
       *  Rendered OUTSIDE ProtectedRoute so visitors (and app-store reviewers) can reach every page. */}
      <Route element={<PublicLayout />}>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/ai-safety" element={<AiSafety />} />
        <Route path="/privacy-choices" element={<PrivacyChoices />} />
        <Route path="/safety" element={<PublicSafety />} />
        <Route path="/support" element={<Support />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/download" element={<Download />} />
      </Route>
      {/* Organisation / HR portal (FLMs get a reduced nav; guarded in AdminShell) */}
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<AdminDashboard />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="organizations" element={<Organizations />} />
        <Route path="teams" element={<Teams />} />
        <Route path="invitations" element={<Invitations />} />
        <Route path="users" element={<Members />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* Platform Owner portal — replaces the legacy SUPER section */}
      <Route path="/owner" element={<OwnerShell />}>
        <Route index element={<OwnerDashboard />} />
        <Route path="organizations" element={<OwnerOrganizations />} />
        <Route path="organizations/:id" element={<OwnerOrgDetail />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="billing" element={<OwnerBilling />} />
        <Route path="usage" element={<AdminUsage />} />
        <Route path="invites" element={<Invitations />} />
        <Route path="feedback" element={<AdminFeedback />} />
        <Route path="beta" element={<AdminBeta />} />
        <Route path="roles" element={<OwnerRoles />} />
        <Route path="audit" element={<OwnerAudit />} />
      </Route>

      {/* Occupational Health (bedrijfsarts) portal — distinct permission model from HR */}
      <Route path="/oh" element={<OhShell />}>
        <Route index element={<OhHome />} />
        <Route path="clients" element={<OhClients />} />
        <Route path="clients/:id" element={<OhClientDetail />} />
      </Route>

      {/* Protected shell */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        {/* Client */}
        <Route index element={<ClientHome />} />
        <Route path="companion" element={<Companion />} />
        <Route path="checkins" element={<CheckIn />} />
        <Route path="plan" element={<Plan />} />
        <Route path="insights" element={<Insights />} />
        <Route path="rhythm" element={<Rhythm />} />
        <Route path="victories" element={<Victories />} />
        <Route path="memories" element={<Memories />} />
        <Route path="reflection" element={<NightReflection />} />
        <Route path="quiet-returns" element={<QuietReturns />} />
        <Route path="craving" element={<Craving />} />
        <Route path="ambient" element={<Ambient />} />
        <Route path="proactive" element={<Proactive />} />
        <Route path="safety" element={<Safety />} />
        <Route path="consent" element={<ConsentCenter />} />
        <Route path="settings" element={<Settings />} />

        {/* Admin */}
        <Route path="admin" element={<ProtectedRoute portal="admin"><AdminHome /></ProtectedRoute>} />
        <Route path="admin/users" element={<ProtectedRoute portal="admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="admin/conversations" element={<ProtectedRoute portal="admin"><AdminConversations /></ProtectedRoute>} />
        <Route path="admin/flagged" element={<ProtectedRoute portal="admin"><AdminFlagged /></ProtectedRoute>} />
        <Route path="admin/invites" element={<ProtectedRoute portal="admin"><AdminInvites /></ProtectedRoute>} />
        <Route path="admin/beta" element={<ProtectedRoute portal="admin"><AdminBeta /></ProtectedRoute>} />
        <Route path="admin/usage" element={<ProtectedRoute portal="admin"><AdminUsage /></ProtectedRoute>} />
        <Route path="admin/feedback" element={<ProtectedRoute portal="admin"><AdminFeedback /></ProtectedRoute>} />

        {/* Business / Platform */}
        <Route path="business" element={<ProtectedRoute portal="business"><PlatformHome /></ProtectedRoute>} />
        <Route path="business/people" element={<ProtectedRoute portal="business"><PlatformPeople /></ProtectedRoute>} />
        <Route path="business/teams" element={<ProtectedRoute portal="business"><PlatformTeams /></ProtectedRoute>} />
        <Route path="business/locations" element={<ProtectedRoute portal="business"><PlatformLocations /></ProtectedRoute>} />
        <Route path="business/departments" element={<ProtectedRoute portal="business"><PlatformDepartments /></ProtectedRoute>} />
        <Route path="business/invites" element={<ProtectedRoute portal="business"><PlatformInvites /></ProtectedRoute>} />
        <Route path="business/analytics" element={<ProtectedRoute portal="business"><PlatformAnalytics /></ProtectedRoute>} />
        <Route path="business/wellbeing" element={<ProtectedRoute portal="business"><PlatformWellbeing /></ProtectedRoute>} />
        <Route path="business/audit" element={<ProtectedRoute portal="business"><PlatformAudit /></ProtectedRoute>} />

        {/* Care / GGZ */}
        <Route path="care" element={<ProtectedRoute portal="care"><CareHome /></ProtectedRoute>} />
        <Route path="care/clients" element={<ProtectedRoute portal="care"><CareClients /></ProtectedRoute>} />
        <Route path="care/clients/:id" element={<ProtectedRoute portal="care"><CareClientDetail /></ProtectedRoute>} />
        <Route path="care/alerts" element={<ProtectedRoute portal="care"><CareAlerts /></ProtectedRoute>} />
        <Route path="care/dossier" element={<ProtectedRoute portal="care"><CareDossier /></ProtectedRoute>} />
      </Route>

      <Route path="/" element={<Entry />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-center" richColors closeButton />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
